import { useNavigate } from '@tanstack/react-router';
import type { Machine } from '@bridgewerk/shared';
import { MachineCard } from './MachineCard.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { Button } from '../../../components/ui/Button.js';

export interface MachineGridProps {
  readonly machines: Machine[] | undefined;
  readonly loading?: boolean;
  readonly error?: unknown;
  readonly onRetry?: () => void;
}

export function MachineGrid({ machines, loading, error, onRetry }: MachineGridProps): JSX.Element {
  const navigate = useNavigate();

  if (loading && !machines) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[0, 1, 2].map((i) => (
          <MachineCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center">
        <p className="text-sm text-red mb-2">Couldn&apos;t load machines.</p>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (!machines || machines.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/30 p-8 text-center">
        <h3 className="font-mono text-sm mb-1">No machines connected yet</h3>
        <p className="text-xs text-text-dim mb-3">
          Plug in a Bridgewerk dongle and the shop floor will populate automatically.
        </p>
        <Button variant="secondary" onClick={() => void navigate({ to: '/settings' })}>
          Set up a machine
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {machines.map((m) => (
        <MachineCard
          key={m.machine_id}
          machine={m}
          onClick={() =>
            void navigate({
              to: '/machines/$machineId',
              params: { machineId: m.machine_id },
              search: { tab: 'overview' },
            })
          }
        />
      ))}
    </div>
  );
}

function MachineCardSkeleton(): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-card overflow-hidden p-4 space-y-3" style={{ borderLeft: '3px solid var(--color-border)' }}>
      <div className="flex justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-16" />
      </div>
      <Skeleton className="h-3 w-40" />
      <div className="grid grid-cols-3 gap-2">
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
        <Skeleton className="h-8" />
      </div>
      <Skeleton className="h-1 w-full" />
    </div>
  );
}
